import React from 'react';
import { ChevronRight } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Button } from '../../ui/button';
import { OrgMembersPanel } from '../OrgMembersPanel';
import { OrgSegmentsPanel } from '../OrgSegmentsPanel';
import OrgSettingsPanel from '../OrgSettingsPanel';
import { OrgListView } from '../OrgListView';
import DeleteOrgDialog from '../DeleteOrgDialog';

/**
 * Body of the Organizations tab in the Admin Dashboard.
 *
 * - When `managingOrgId` is null, shows the org list.
 * - When set, shows the management view with nested Members / Cohorts &
 *   Programs / Settings tabs and (for super-admins) the delete-org
 *   confirmation dialog.
 */
export default function OrgManagementView({
  managingOrgId,
  setManagingOrgId,
  organizations,
  users,
  currentUser,
  authIsSuperAdmin,
  fetchUsers,
  deleteOrgDialogOpen,
  setDeleteOrgDialogOpen,
  deleteOrgConfirmText,
  setDeleteOrgConfirmText,
  handleDeleteOrg,
  deletingOrg,
  pulsingOrgId,
  onOrgSettingsSaved,
  onBackToOrgList,
}) {
  if (!managingOrgId) {
    return (
      <OrgListView
        organizations={organizations}
        setManagingOrgId={setManagingOrgId}
        pulsingOrgId={pulsingOrgId}
      />
    );
  }

  const org = organizations.find((o) => o.id === managingOrgId);
  const orgName = org?.name || 'Organization';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => (onBackToOrgList ? onBackToOrgList() : setManagingOrgId(null))}
            data-testid="back-to-orgs-btn"
          >
            <ChevronRight size={14} className="mr-1 rotate-180" />
            Back to Organizations
          </Button>
          <span className="text-sm text-gray-400">|</span>
          <h3 className="font-semibold text-gray-900">{orgName}</h3>
        </div>
        {currentUser?.is_super_admin && (
          <DeleteOrgDialog
            open={deleteOrgDialogOpen}
            onOpenChange={(open) => {
              setDeleteOrgDialogOpen(open);
              if (!open) setDeleteOrgConfirmText('');
            }}
            orgName={orgName}
            userCount={org?.user_count || 0}
            confirmText={deleteOrgConfirmText}
            setConfirmText={setDeleteOrgConfirmText}
            onDelete={handleDeleteOrg}
            deleting={deletingOrg}
          />
        )}
      </div>

      <Tabs defaultValue="members" className="w-full">
        <TabsList>
          <TabsTrigger value="members" data-testid="org-tab-members">
            Members
          </TabsTrigger>
          <TabsTrigger value="segments" data-testid="org-tab-segments">
            Cohorts & Programs
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="org-tab-settings">
            Settings
          </TabsTrigger>
        </TabsList>
        <TabsContent value="members">
          <OrgMembersPanel
            orgId={managingOrgId}
            organizations={organizations}
            users={users}
            isSuperAdmin={authIsSuperAdmin}
            onMembersChanged={fetchUsers}
          />
        </TabsContent>
        <TabsContent value="segments">
          <OrgSegmentsPanel orgId={managingOrgId} orgName={orgName} />
        </TabsContent>
        <TabsContent value="settings">
          <OrgSettingsPanel orgId={managingOrgId} onSaved={onOrgSettingsSaved} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
